import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Download,
  Upload,
  Database,
  RefreshCw,
  PlayCircle,
  HardDrive,
  AlertCircle,
} from "lucide-react";
import { API } from "@/lib/api";
import { client } from "@/lib/api-base";

const TABLE_LABELS = {
  vendors: "Fornitori",
  categories: "Categorie",
  expenses: "Serie ricorrenti",
  expense_occurrences: "Scadenze",
  payments: "Pagamenti",
};

function fmtBytes(n) {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
}

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("it-IT", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Backup / Restore tab for the Anagrafiche page.
 *
 * Talks directly to /api/admin/backup/info, /api/admin/backup and
 * /api/admin/restore. Uses raw axios for the upload + download flows because
 * the lib/api.js wrapper unwraps response.data and we need responseType blob
 * for the download and FormData for the upload.
 */
export function BackupTab({ testId = "tab-backup-content" }) {
  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const fileRef = useRef(null);

  const refreshInfo = async () => {
    setLoadingInfo(true);
    try {
      const res = await client.get("/admin/backup/info");
      setInfo(res.data);
    } catch (e) {
      toast.error("Impossibile leggere lo stato del database.");
    } finally {
      setLoadingInfo(false);
    }
  };

  useEffect(() => {
    refreshInfo();
  }, []);

  const downloadBackup = async () => {
    setDownloading(true);
    try {
      const res = await client.get("/admin/backup", {
        responseType: "blob",
      });
      // Extract filename from Content-Disposition or fall back to a sensible default.
      const cd = res.headers["content-disposition"] || "";
      const match = /filename="?([^"]+)"?/i.exec(cd);
      const ts = new Date().toISOString().replace(/[-:T.Z]/g, "").slice(0, 14);
      const filename = match ? match[1] : `pgi-backup-${ts}.db`;
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Backup scaricato.");
    } catch (e) {
      toast.error("Errore durante il download del backup.");
    } finally {
      setDownloading(false);
    }
  };

  const handleFilePick = (e) => {
    const f = e.target.files?.[0] || null;
    setRestoreFile(f);
    setRestoreResult(null);
  };

  const triggerRestore = () => {
    if (!restoreFile) {
      toast.error("Seleziona prima un file di backup.");
      return;
    }
    setConfirmOpen(true);
  };

  const doRestore = async () => {
    setConfirmOpen(false);
    setRestoring(true);
    setRestoreResult(null);
    try {
      const fd = new FormData();
      fd.append("file", restoreFile);
      const res = await client.post("/admin/restore", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setRestoreResult(res.data);
      toast.success("Ripristino completato.");
      setRestoreFile(null);
      if (fileRef.current) fileRef.current.value = "";
      refreshInfo();
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        "Ripristino fallito. Il database originale è stato preservato.";
      toast.error(msg);
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div data-testid={testId} className="space-y-6">
      {/* Status card */}
      <div className="rounded-xl border border-stone-200 bg-white p-5">
        <div className="mb-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Database size={16} className="text-stone-600" strokeWidth={1.8} />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-stone-600">
              Stato del database
            </h2>
          </div>
          <Button
            size="sm"
            variant="ghost"
            data-testid="btn-refresh-backup-info"
            onClick={refreshInfo}
            disabled={loadingInfo}
            className="h-8"
          >
            <RefreshCw
              size={14}
              strokeWidth={1.8}
              className={"mr-1 " + (loadingInfo ? "animate-spin" : "")}
            />
            Aggiorna
          </Button>
        </div>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-stone-500">
              Dimensione
            </div>
            <div
              data-testid="backup-size"
              className="font-mono text-base font-semibold tabular-nums text-stone-900"
            >
              {fmtBytes(info?.size_bytes)}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-stone-500">
              Ultima modifica
            </div>
            <div
              data-testid="backup-modified-at"
              className="font-mono text-base font-semibold tabular-nums text-stone-900"
            >
              {fmtDateTime(info?.modified_at)}
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-stone-500">
              Record totali
            </div>
            <div
              data-testid="backup-total-rows"
              className="font-mono text-base font-semibold tabular-nums text-stone-900"
            >
              {info
                ? Object.values(info.tables || {}).reduce(
                    (s, n) => s + Number(n || 0),
                    0,
                  )
                : "—"}
            </div>
          </div>
        </div>

        {info?.tables && (
          <div className="mt-4 overflow-hidden rounded-lg border border-stone-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tabella</TableHead>
                  <TableHead className="text-right">Record</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(info.tables).map(([t, n]) => (
                  <TableRow key={t} data-testid={`backup-row-${t}`}>
                    <TableCell className="text-stone-700">
                      {TABLE_LABELS[t] || t}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {n}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {/* Backup card */}
      <div className="rounded-xl border border-stone-200 bg-white p-5">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wider text-stone-600">
              Scarica backup
            </h2>
            <p className="mt-1 text-xs text-stone-500">
              Esporta un'istantanea completa del database (SQLite) — un singolo
              file <strong>.db</strong> che contiene tutti gli archivi:
              categorie, fornitori, serie ricorrenti, scadenze e pagamenti.
              Conservalo in un luogo sicuro prima di operazioni importanti.
            </p>
          </div>
          <Button
            data-testid="btn-download-backup"
            onClick={downloadBackup}
            disabled={downloading}
          >
            <Download size={14} strokeWidth={1.8} className="mr-1.5" />
            {downloading ? "Generazione…" : "Scarica backup"}
          </Button>
        </div>
      </div>

      {/* Auto-backup card */}
      <AutoBackupCard />

      {/* Restore card */}
      <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-amber-900">
          Ripristina da backup
        </h2>
        <p className="mt-1 text-xs text-amber-900/80">
          Carica un file <strong>.db</strong> precedentemente scaricato. Il
          database attuale verrà <strong>completamente sostituito</strong>;
          prima della sostituzione il file corrente viene salvato a parte come
          copia di sicurezza recuperabile.
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".db,.sqlite,.sqlite3"
            data-testid="input-restore-file"
            onChange={handleFilePick}
            className="block w-full max-w-md text-sm text-stone-700 file:mr-3 file:rounded-md file:border file:border-stone-300 file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-stone-700 hover:file:bg-stone-50"
          />
          <Button
            variant="destructive"
            data-testid="btn-restore"
            onClick={triggerRestore}
            disabled={!restoreFile || restoring}
          >
            <Upload size={14} strokeWidth={1.8} className="mr-1.5" />
            {restoring ? "Ripristino in corso…" : "Carica e ripristina"}
          </Button>
        </div>

        {restoreFile && (
          <p className="mt-2 text-[11px] text-amber-900">
            File selezionato: <strong>{restoreFile.name}</strong> (
            {fmtBytes(restoreFile.size)})
          </p>
        )}

        {restoreResult && (
          <div
            data-testid="restore-result"
            className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-xs text-emerald-900"
          >
            <strong>Ripristino completato.</strong> Database aggiornato con i
            record:{" "}
            {Object.entries(restoreResult.tables || {})
              .map(([k, v]) => `${TABLE_LABELS[k] || k} ${v}`)
              .join(" · ")}
            . La copia precedente è conservata sul server come{" "}
            <code className="font-mono">{restoreResult.previous_backup_kept_at}</code>
            .
          </div>
        )}
      </div>

      {/* Restore confirmation */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="dialog-restore-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Confermare il ripristino?</AlertDialogTitle>
            <AlertDialogDescription>
              Tutti i dati attualmente presenti verranno sostituiti dal
              contenuto del file <strong>{restoreFile?.name}</strong>. Il
              database attuale viene comunque salvato sul server come copia di
              sicurezza recuperabile prima della sostituzione.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="btn-confirm-restore"
              onClick={doRestore}
            >
              Ripristina ora
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}



/* -------------------------------------------------------------------------- */
/*  Automatic backup rotation                                                  */
/* -------------------------------------------------------------------------- */
function AutoBackupCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [toggling, setToggling] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await client.get("/admin/auto-backup");
      setStatus(res.data);
    } catch (e) {
      toast.error("Impossibile leggere lo stato del backup automatico.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const toggleEnabled = async (next) => {
    setToggling(true);
    try {
      const res = await client.patch("/admin/auto-backup", {
        enabled: next,
      });
      setStatus(res.data);
      toast.success(
        next ? "Backup automatico attivato." : "Backup automatico disattivato.",
      );
    } catch (e) {
      toast.error("Impossibile aggiornare le impostazioni.");
    } finally {
      setToggling(false);
    }
  };

  const runNow = async (kind) => {
    setRunning(true);
    try {
      const res = await client.post("/admin/auto-backup/run", null, {
        params: { kind },
      });
      setStatus(res.data.status);
      if (res.data.run?.status === "ok") {
        toast.success(
          `Backup automatico (${kind === "monthly" ? "mensile" : "giornaliero"}) completato.`,
        );
      } else {
        toast.error(
          res.data.run?.error || "Backup automatico fallito. Vedi log.",
        );
      }
    } catch (e) {
      toast.error(
        e?.response?.data?.run?.error ||
          e?.response?.data?.detail ||
          "Backup automatico fallito.",
      );
      // Refresh anyway so the UI shows the latest status / error.
      refresh();
    } finally {
      setRunning(false);
    }
  };

  const downloadFile = async (filename) => {
    try {
      const res = await client.get(
        `/admin/auto-backup/file/${encodeURIComponent(filename)}`,
        { responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Download non riuscito.");
    }
  };

  const enabled = status?.enabled ?? false;
  const lastOk = status?.last_status === "ok";
  const lastError = status?.last_status === "error";

  return (
    <div
      data-testid="auto-backup-card"
      className="rounded-xl border border-stone-200 bg-white p-5"
    >
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <HardDrive
              size={16}
              strokeWidth={1.8}
              className="text-stone-600"
            />
            <h2 className="text-sm font-semibold uppercase tracking-wider text-stone-600">
              Backup automatico
            </h2>
            <span
              data-testid="auto-backup-state-badge"
              className={
                "inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider " +
                (enabled
                  ? "bg-emerald-600 text-white"
                  : "bg-stone-300 text-stone-700")
              }
            >
              {enabled ? "Attivo" : "Disattivo"}
            </span>
          </div>
          <p className="mt-1 max-w-2xl text-xs text-stone-500">
            Snapshot automatici su disco. Vengono conservati gli ultimi{" "}
            <strong>{status?.keep_daily ?? 7} backup giornalieri</strong> e gli
            ultimi <strong>{status?.keep_monthly ?? 12} backup mensili</strong>;
            i più vecchi vengono rimossi automaticamente.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-[11px] uppercase tracking-wider text-stone-500">
            {enabled ? "Disattiva" : "Attiva"}
          </span>
          <Switch
            data-testid="auto-backup-toggle"
            checked={enabled}
            disabled={toggling}
            onCheckedChange={toggleEnabled}
          />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-stone-500">
            Ultimo backup
          </div>
          <div
            data-testid="auto-backup-last"
            className={
              "font-mono text-sm font-semibold tabular-nums " +
              (lastError ? "text-rose-700" : "text-stone-900")
            }
          >
            {fmtDateTime(status?.last_run_at)}
            {status?.last_kind && (
              <span className="ml-1 text-[10px] uppercase tracking-wider text-stone-400">
                ({status.last_kind === "monthly" ? "mensile" : "giorn."})
              </span>
            )}
          </div>
          {lastError && (
            <div className="mt-1 flex items-start gap-1 text-[11px] text-rose-700">
              <AlertCircle size={12} className="mt-0.5 shrink-0" />
              <span>{status?.last_error}</span>
            </div>
          )}
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wider text-stone-500">
            Backup disponibili
          </div>
          <div
            data-testid="auto-backup-count"
            className="font-mono text-sm font-semibold tabular-nums text-stone-900"
          >
            {status ? `${status.count} totali` : "—"}
            {status && (
              <span className="ml-1 text-[10px] uppercase tracking-wider text-stone-400">
                ({status.daily_count} giorn. · {status.monthly_count} mens.)
              </span>
            )}
          </div>
        </div>
        <div className="md:col-span-2">
          <div className="text-[11px] uppercase tracking-wider text-stone-500">
            Cartella
          </div>
          <div
            data-testid="auto-backup-dir"
            className="break-all font-mono text-xs text-stone-700"
          >
            {status?.directory || "—"}
          </div>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          data-testid="btn-run-auto-daily"
          onClick={() => runNow("daily")}
          disabled={running || loading}
        >
          <PlayCircle size={14} strokeWidth={1.8} className="mr-1.5" />
          Esegui ora (giornaliero)
        </Button>
        <Button
          size="sm"
          variant="outline"
          data-testid="btn-run-auto-monthly"
          onClick={() => runNow("monthly")}
          disabled={running || loading}
        >
          <PlayCircle size={14} strokeWidth={1.8} className="mr-1.5" />
          Esegui ora (mensile)
        </Button>
        <Button
          size="sm"
          variant="ghost"
          data-testid="btn-refresh-auto"
          onClick={refresh}
          disabled={loading}
          className="ml-auto"
        >
          <RefreshCw
            size={14}
            strokeWidth={1.8}
            className={"mr-1 " + (loading ? "animate-spin" : "")}
          />
          Aggiorna
        </Button>
      </div>

      {(status?.files || []).length > 0 && (
        <div className="mt-4 overflow-hidden rounded-lg border border-stone-200">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>File</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead className="text-right">Dimensione</TableHead>
                <TableHead>Creato</TableHead>
                <TableHead className="w-[1%]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {status.files.map((f) => (
                <TableRow
                  key={f.filename}
                  data-testid={`auto-backup-file-${f.filename}`}
                >
                  <TableCell className="font-mono text-xs text-stone-700">
                    {f.filename}
                  </TableCell>
                  <TableCell>
                    <span
                      className={
                        "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-medium uppercase " +
                        (f.kind === "monthly"
                          ? "border-amber-200 bg-amber-50 text-amber-800"
                          : "border-blue-200 bg-blue-50 text-blue-700")
                      }
                    >
                      {f.kind === "monthly" ? "Mensile" : "Giornaliero"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono tabular-nums text-xs text-stone-700">
                    {fmtBytes(f.size_bytes)}
                  </TableCell>
                  <TableCell className="font-mono tabular-nums text-xs text-stone-700">
                    {fmtDateTime(f.modified_at)}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-7"
                      onClick={() => downloadFile(f.filename)}
                      data-testid={`btn-download-auto-${f.filename}`}
                    >
                      <Download size={12} className="mr-1" />
                      Scarica
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
